/**
 * Field Reports Page
 * BuilderBel Construction Project Management System
 */

import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileCheck,
  FileText,
  MapPin,
  Pencil,
  Plus,
  Send,
  ThumbsUp,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input, SearchableDropdown, TextArea } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { FieldReportDrawer } from '../components/fieldReports';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useApproveFieldReportMutation,
  useDeleteFieldReportMutation,
  useGetFieldReportStatsQuery,
  useGetFieldReportsQuery,
  useRejectFieldReportMutation,
  useReviewFieldReportMutation,
  useSubmitFieldReportMutation,
} from '../services/fieldReportApi';
import type { FieldReport, FieldReportStatus, FieldReportPriority, FieldReportType } from '../types/fieldReport';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'general', label: 'General' },
  { value: 'progress', label: 'Progress' },
  { value: 'observation', label: 'Observation' },
  { value: 'issue', label: 'Issue' },
  { value: 'site_condition', label: 'Site Condition' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const SORT_OPTIONS = [
  { value: 'reportDate', label: 'Report Date' },
  { value: 'createdAt', label: 'Created Date' },
  { value: 'title', label: 'Title' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: FieldReportStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'submitted':
      return 'info';
    case 'reviewed':
      return 'purple';
    case 'rejected':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

const getPriorityVariant = (
  priority: FieldReportPriority
): 'error' | 'warning' | 'info' | 'neutral' => {
  switch (priority) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
    default:
      return 'neutral';
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatReportType = (type: FieldReportType): string => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// ============================================================================
// COMPONENT
// ============================================================================

const FieldReports: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<FieldReportStatus | ''>('');
  const [reportType, setReportType] = useState<FieldReportType | ''>('');
  const [priority, setPriority] = useState<FieldReportPriority | ''>('');
  const [sortBy, setSortBy] = useState('reportDate');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<FieldReport | null>(null);

  // Reject modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingReport, setRejectingReport] = useState<FieldReport | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Queries
  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      ...(status && { status }),
      ...(reportType && { reportType }),
      ...(priority && { priority }),
      ...(search && { search }),
      sortBy,
      sortOrder,
    }),
    [page, status, reportType, priority, search, sortBy, sortOrder]
  );

  const { data: listData, isLoading } = useGetFieldReportsQuery(
    { projectId: projectId || '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetFieldReportStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  // Mutations
  const [deleteReport] = useDeleteFieldReportMutation();
  const [submitReport] = useSubmitFieldReportMutation();
  const [reviewReport] = useReviewFieldReportMutation();
  const [approveReport] = useApproveFieldReportMutation();
  const [rejectReport] = useRejectFieldReportMutation();

  const fieldReports = listData?.data?.fieldReports || [];
  const pagination = listData?.data?.pagination;
  const stats = statsData?.data;

  // ---- Handlers ----

  const handleCreate = () => {
    setSelectedReport(null);
    setDrawerOpen(true);
  };

  const handleEdit = useCallback((report: FieldReport) => {
    setSelectedReport(report);
    setDrawerOpen(true);
  }, []);

  const handleView = useCallback((report: FieldReport) => {
    setSelectedReport(report);
    setDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (report: FieldReport) => {
      if (!projectId) return;
      if (!window.confirm(`Delete field report "${report.title}"? This cannot be undone.`)) return;
      try {
        await deleteReport({ projectId, reportId: report._id }).unwrap();
        toast.success('Field report deleted');
      } catch {
        toast.error('Failed to delete field report');
      }
    },
    [projectId, deleteReport, toast]
  );

  const handleSubmit = useCallback(
    async (report: FieldReport) => {
      if (!projectId) return;
      try {
        await submitReport({ projectId, reportId: report._id }).unwrap();
        toast.success('Field report submitted for review');
      } catch {
        toast.error('Failed to submit field report');
      }
    },
    [projectId, submitReport, toast]
  );

  const handleReview = useCallback(
    async (report: FieldReport) => {
      if (!projectId) return;
      try {
        await reviewReport({ projectId, reportId: report._id, data: {} }).unwrap();
        toast.success('Field report marked as reviewed');
      } catch {
        toast.error('Failed to review field report');
      }
    },
    [projectId, reviewReport, toast]
  );

  const handleApprove = useCallback(
    async (report: FieldReport) => {
      if (!projectId) return;
      try {
        await approveReport({ projectId, reportId: report._id, data: {} }).unwrap();
        toast.success('Field report approved');
      } catch {
        toast.error('Failed to approve field report');
      }
    },
    [projectId, approveReport, toast]
  );

  const handleOpenRejectModal = useCallback((report: FieldReport) => {
    setRejectingReport(report);
    setRejectionReason('');
    setRejectModalOpen(true);
  }, []);

  const handleReject = useCallback(async () => {
    if (!projectId || !rejectingReport || !rejectionReason.trim()) return;
    try {
      await rejectReport({
        projectId,
        reportId: rejectingReport._id,
        data: { rejectionReason: rejectionReason.trim() },
      }).unwrap();
      toast.success('Field report rejected');
      setRejectModalOpen(false);
      setRejectingReport(null);
      setRejectionReason('');
    } catch {
      toast.error('Failed to reject field report');
    }
  }, [projectId, rejectingReport, rejectionReason, rejectReport, toast]);

  const handleDownloadPdf = useCallback(
    async (report: FieldReport) => {
      if (!projectId) return;
      try {
        const token = localStorage.getItem('auth_token');
        const url = `/api/projects/${projectId}/field-reports/${report._id}/pdf`;
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('PDF generation failed');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `field-report-${report.reportNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // Build action menu items for each row
  const getActions = useCallback(
    (report: FieldReport): (ActionMenuItem | 'divider')[] => {
      const isDraftOrRejected = ['draft', 'rejected'].includes(report.status);
      const isSubmitted = report.status === 'submitted';
      const isReviewed = report.status === 'reviewed';

      const items: (ActionMenuItem | 'divider')[] = [
        {
          label: 'View Details',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleView(report),
        },
      ];

      if (isDraftOrRejected) {
        items.push({
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleEdit(report),
        });
      }

      if (report.status === 'draft') {
        items.push({
          label: 'Submit for Review',
          icon: <Send className="h-4 w-4" />,
          onClick: () => handleSubmit(report),
        });
      }

      if (isSubmitted) {
        items.push({
          label: 'Mark as Reviewed',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleReview(report),
        });
      }

      if (isReviewed) {
        items.push({
          label: 'Approve',
          icon: <ThumbsUp className="h-4 w-4" />,
          onClick: () => handleApprove(report),
          variant: 'success',
        });
        items.push({
          label: 'Reject',
          icon: <XCircle className="h-4 w-4" />,
          onClick: () => handleOpenRejectModal(report),
          variant: 'danger',
        });
      }

      items.push('divider');
      items.push({
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPdf(report),
      });

      if (isDraftOrRejected) {
        items.push('divider');
        items.push({
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(report),
          variant: 'danger',
        });
      }

      return items;
    },
    [
      handleView,
      handleEdit,
      handleSubmit,
      handleReview,
      handleApprove,
      handleOpenRejectModal,
      handleDownloadPdf,
      handleDelete,
    ]
  );

  // ---- Stats tiles ----

  const statsTiles = useMemo(
    () => [
      {
        title: 'Total Reports',
        value: stats?.total || 0,
        icon: <FileText className="h-4 w-4 text-brand-600" />,
      },
      {
        title: 'Open Issues',
        value: stats?.openIssues || 0,
        icon: <AlertTriangle className="h-4 w-4 text-amber-500" />,
      },
      {
        title: 'Approved',
        value: stats?.approved || 0,
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
      },
      {
        title: 'Pending Review',
        value: (stats?.submitted || 0) + (stats?.reviewed || 0),
        icon: <Clock className="h-4 w-4 text-blue-500" />,
      },
    ],
    [stats]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!projectId) {
    return (
      <ModuleLayout
        title="Field Reports"
        description="Document field observations and progress for this project"
        icon={<FileCheck className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={FileCheck}
          title="No project selected"
          description="Select a project to view field reports."
        />
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout
      title="Field Reports"
      description="Document field observations, progress, and site conditions"
      icon={<FileCheck className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Create Report',
        onClick: handleCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={statsTiles}
    >
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[200px] max-w-xs">
          <Input
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search reports..."
          />
        </div>
        <div className="w-40">
          <SearchableDropdown
            options={STATUS_OPTIONS}
            value={status}
            onChange={val => {
              setStatus(val as FieldReportStatus | '');
              setPage(1);
            }}
            placeholder="Status"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={TYPE_OPTIONS}
            value={reportType}
            onChange={val => {
              setReportType(val as FieldReportType | '');
              setPage(1);
            }}
            placeholder="Type"
          />
        </div>
        <div className="w-40">
          <SearchableDropdown
            options={PRIORITY_OPTIONS}
            value={priority}
            onChange={val => {
              setPriority(val as FieldReportPriority | '');
              setPage(1);
            }}
            placeholder="Priority"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={val => setSortBy(val)}
            placeholder="Sort by"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      ) : fieldReports.length === 0 ? (
        <EmptyState
          icon={FileCheck}
          title="No field reports found"
          description={
            search || status || reportType || priority
              ? 'No reports match your current filters. Try adjusting them.'
              : 'Create your first field report to document site observations and progress.'
          }
          action={
            !search && !status && !reportType && !priority
              ? { label: 'Create Field Report', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Report
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Type
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Location
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Priority
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                    Issues
                  </th>
                  <th className="w-10 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {fieldReports.map(report => {
                  const openIssues = report.issues?.filter(i => i.status !== 'resolved').length || 0;
                  return (
                    <tr
                      key={report._id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleView(report)}
                    >
                      {/* Report number + title */}
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 leading-tight">
                              {report.title}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">{report.reportNumber}</p>
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {formatReportType(report.reportType)}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {formatDate(report.reportDate)}
                        </span>
                      </td>

                      {/* Location */}
                      <td className="px-4 py-3">
                        {report.location ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-600 truncate max-w-[140px]">
                              {report.location}
                              {report.area ? ` — ${report.area}` : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>

                      {/* Priority */}
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={report.priority.charAt(0).toUpperCase() + report.priority.slice(1)}
                          variant={getPriorityVariant(report.priority)}
                        />
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                          variant={getStatusVariant(report.status)}
                        />
                      </td>

                      {/* Issues */}
                      <td className="px-4 py-3">
                        {openIssues > 0 ? (
                          <div className="flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                            <span className="text-sm text-amber-700 font-medium">
                              {openIssues}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td
                        className="px-4 py-3"
                        onClick={e => e.stopPropagation()}
                      >
                        <ActionMenu id={report._id} items={getActions(report)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} reports
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Drawer */}
      <FieldReportDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedReport(null);
        }}
        projectId={projectId || ''}
        report={selectedReport}
      />

      {/* Reject Modal */}
      {rejectModalOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[110]" />
            <div className="fixed inset-0 flex items-center justify-center z-[120] p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Reject Field Report</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Provide a reason for rejecting this report.
                  </p>
                </div>
                <div className="px-6 py-5">
                  <label className="block text-xs text-gray-500 mb-2">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <TextArea
                    value={rejectionReason}
                    onChange={setRejectionReason}
                    rows={4}
                    placeholder="Explain why this report is being rejected..."
                  />
                </div>
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
                  <button
                    onClick={() => {
                      setRejectModalOpen(false);
                      setRejectingReport(null);
                      setRejectionReason('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={!rejectionReason.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reject Report
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </ModuleLayout>
  );
};

export default FieldReports;
